import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import {
  CheckCircle2, ClipboardCheck, Phone, FileText,
  MessageCircle, Image as ImageIcon, Download, Instagram, ArrowRight,
} from "lucide-react";
import Navbar from "../components/sections/Navbar";
import Footer from "../components/sections/Footer";
import FloatingWhatsApp from "../components/sections/FloatingWhatsApp";
import { WHATSAPP_NUMBER_RAW, BRAND } from "../lib/constants";

const fmtMxn = (n) => {
  if (n == null) return null;
  return `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })} MXN`;
};

export default function Gracias() {
  const location = useLocation();
  const lead = location.state || {};
  const hasLead = Boolean(lead.lead_id);
  const firstName = (lead.full_name || "").trim().split(" ")[0] || "";

  // Noindex + dataLayer push + confetti
  useEffect(() => {
    // SEO: noindex,nofollow — update existing meta if present, otherwise add one
    const existing = document.querySelector('meta[name="robots"]');
    const prevContent = existing?.getAttribute("content");
    let meta = existing;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    const prevTitle = document.title;
    document.title = "Gracias por tu solicitud | Velarias Tens";

    // Canonical
    const canonicalEl = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonicalEl?.getAttribute("href");
    if (canonicalEl) canonicalEl.setAttribute("href", "https://velariastens.com.mx/gracias");

    // Description override
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute("content");
    if (descEl) descEl.setAttribute("content", "Recibimos tu solicitud de cotización. Un ingeniero te contactará en menos de 24 horas.");

    // GTM conversion event
    if (typeof window !== "undefined" && hasLead) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "lead_submit_success",
        lead_id: lead.lead_id,
        lead_score: lead.lead_score,
        lead_tier: lead.lead_tier,
        project_type: lead.project_type,
        conversion_value: lead.estimated_value_mxn || 0,
        currency: "MXN",
        page_path: "/gracias",
      });
    }

    // Subtle confetti burst
    const fire = (origin) =>
      confetti({
        particleCount: 60,
        spread: 70,
        startVelocity: 35,
        ticks: 200,
        origin,
        colors: ["#8cb62c", "#3D5311", "#FFFFFF", "#F0F4DC"],
        gravity: 0.9,
        scalar: 0.9,
      });
    const t1 = setTimeout(() => fire({ x: 0.25, y: 0.35 }), 200);
    const t2 = setTimeout(() => fire({ x: 0.75, y: 0.35 }), 500);
    const t3 = setTimeout(() => fire({ x: 0.5, y: 0.3 }), 900);

    return () => {
      if (existing && prevContent != null) {
        existing.setAttribute("content", prevContent);
      } else if (meta && !existing) {
        document.head.removeChild(meta);
      }
      if (canonicalEl && prevCanonical != null) canonicalEl.setAttribute("href", prevCanonical);
      if (descEl && prevDesc != null) descEl.setAttribute("content", prevDesc);
      document.title = prevTitle;
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waMessage = encodeURIComponent(
    hasLead && lead.project_type_label
      ? `Hola, acabo de enviar mi solicitud de cotización para un proyecto de ${lead.project_type_label}. Me gustaría más información.`
      : `Hola, acabo de enviar mi solicitud de cotización en el sitio web. Me gustaría más información.`
  );
  const waUrl = `https://wa.me/${WHATSAPP_NUMBER_RAW}?text=${waMessage}`;

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut", delay },
  });

  const shortId = hasLead ? String(lead.lead_id).slice(0, 8).toUpperCase() : null;

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1F2937]" data-testid="gracias-page">
      <Navbar />

      {/* ===== 1) Hero verde profundo ===== */}
      <section
        className="relative bg-[#3D5311] text-white pt-32 pb-20 md:pt-40 md:pb-28 min-h-[60vh] flex items-center"
        data-testid="gracias-hero"
      >
        <div className="container-tight w-full text-center">
          <motion.div {...fadeUp(0)} className="flex justify-center">
            <CheckCircle2 className="w-24 h-24 text-[#8cb62c]" strokeWidth={1.5} />
          </motion.div>

          <motion.p
            {...fadeUp(0.15)}
            className="mt-6 font-mono text-[11px] md:text-[12px] uppercase tracking-[0.22em] text-[#8cb62c] font-semibold"
            data-testid="gracias-kicker"
          >
            {shortId ? `SOLICITUD RECIBIDA · #${shortId}` : "SOLICITUD RECIBIDA"}
          </motion.p>

          <motion.h1
            {...fadeUp(0.3)}
            className="mt-4 font-display font-black tracking-tightest text-white leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            data-testid="gracias-h1"
          >
            {firstName ? <>Gracias,{" "}<span className="text-[#8cb62c]">{firstName}.</span></> : "Gracias por tu solicitud"}
          </motion.h1>

          <motion.p
            {...fadeUp(0.45)}
            className="mt-6 max-w-2xl mx-auto text-white/85 text-base md:text-[19px] leading-relaxed"
            data-testid="gracias-sub"
          >
            Recibimos los detalles de tu proyecto. Un ingeniero especialista te contactará en menos de <strong className="text-white">24 horas hábiles</strong>.
          </motion.p>
        </div>
      </section>

      {/* ===== 2) Próximos pasos ===== */}
      <section className="bg-[#FAFAF7] py-16 md:py-20" data-testid="gracias-steps">
        <div className="container-tight">
          <motion.div {...fadeUp(0)} className="text-center mb-12">
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#1F2937]">
              ¿Qué sigue ahora?
            </h2>
            <p className="mt-3 text-[#6B7280] max-w-xl mx-auto">
              Te acompañamos en cada paso del proceso. Esto es lo que pasa después de tu solicitud.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                n: "01", title: "Análisis técnico", Icon: ClipboardCheck,
                desc: "Nuestro ingeniero revisa tu solicitud y prepara una primera evaluación de viabilidad.",
                timing: "Dentro de las próximas 2-4 horas hábiles",
              },
              {
                n: "02", title: "Llamada o videollamada", Icon: Phone,
                desc: "Un especialista te contactará para entender mejor tu espacio, uso esperado y agendar visita técnica si aplica.",
                timing: "En menos de 24 horas hábiles",
              },
              {
                n: "03", title: "Cotización formal", Icon: FileText,
                desc: "Recibes una cotización detallada con materiales, tiempos, planes de pago y memoria técnica.",
                timing: "3-5 días hábiles desde el contacto inicial",
              },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                {...fadeUp(0.15 + i * 0.12)}
                className="bg-white border border-[#DDE2D5] p-7 md:p-8 relative"
                data-testid={`gracias-step-${s.n}`}
              >
                <div className="absolute top-0 left-0 w-[3px] h-12 bg-[#8cb62c]" />
                <div className="flex items-start justify-between mb-5">
                  <s.Icon className="w-9 h-9 text-[#8cb62c]" strokeWidth={1.5} />
                  <span className="font-mono text-[11px] text-[#6B7280] tracking-widest">{s.n}</span>
                </div>
                <h3 className="font-display font-extrabold text-xl text-[#1F2937] leading-tight">{s.title}</h3>
                <p className="mt-3 text-[#4B5563] text-[15px] leading-relaxed">{s.desc}</p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.12em] text-[#3D5311] font-semibold">
                  {s.timing}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3) Atajo WhatsApp ===== */}
      <section className="bg-[#F0F4DC] py-14 md:py-16 border-y border-[#DDE2D5]" data-testid="gracias-whatsapp">
        <div className="container-tight grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
          <div className="flex items-start gap-5">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#25D366] grid place-items-center">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#1F2937] leading-tight">
                ¿No quieres esperar?
              </h2>
              <p className="mt-2 text-[#3D5311] text-[15px] leading-relaxed max-w-md">
                Habla con nuestro equipo ahora mismo por WhatsApp. Estamos disponibles para resolver cualquier duda inmediata.
              </p>
            </div>
          </div>
          <div className="md:text-right">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 w-full md:w-auto px-8 py-5 bg-[#25D366] text-white font-display font-bold uppercase tracking-wide text-sm hover:bg-[#1FB358] transition-colors"
              style={{ boxShadow: "0 8px 24px rgba(37,211,102,0.30)" }}
              data-testid="gracias-cta-whatsapp"
            >
              Hablar por WhatsApp
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== 5) Estimación preliminar (condicional) ===== */}
      {hasLead && lead.estimated_value_mxn ? (
        <section className="bg-[#1F2937] text-white py-14 md:py-16" data-testid="gracias-estimate">
          <div className="container-tight">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#8cb62c] font-semibold">
              ESTIMACIÓN PRELIMINAR
            </p>
            <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
              Basado en los datos que nos compartiste, tu proyecto se ubica en un rango estimado de:
            </p>
            <p className="mt-6 font-display font-black text-[#8cb62c] text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight" data-testid="gracias-estimate-range">
              {fmtMxn(lead.estimated_value_mxn?.min || lead.estimated_value_mxn)}
              {lead.estimated_value_mxn?.max ? (
                <> <span className="text-white/40 font-normal mx-1">–</span> {fmtMxn(lead.estimated_value_mxn.max)}</>
              ) : null}
            </p>
            <p className="mt-5 text-white/60 text-sm md:text-[15px] max-w-2xl leading-relaxed">
              Este rango es una estimación automática. La cotización final se ajusta tras visita técnica y depende de materiales específicos, condiciones de sitio y diseño estructural.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-7 inline-flex items-center gap-2 px-6 py-3 bg-[#8cb62c] text-white font-display font-bold uppercase tracking-wide text-xs hover:bg-[#7BA322] transition-colors"
            >
              Hablar con un ingeniero sobre esto
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </section>
      ) : null}

      {/* ===== 4) Mientras esperas ===== */}
      <section className="bg-white py-16 md:py-20" data-testid="gracias-explore">
        <div className="container-tight">
          <motion.h2
            {...fadeUp(0)}
            className="font-display font-black text-2xl md:text-3xl text-[#1F2937]"
          >
            Mientras tanto, <span className="text-[#8cb62c] font-editorial italic">explora nuestro trabajo</span>
          </motion.h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                Icon: ImageIcon,
                title: "Galería de proyectos",
                desc: "+100 proyectos realizados en México y Latinoamérica.",
                cta: "Ver galería",
                href: "/#proyectos",
                external: false,
              },
              {
                Icon: Download,
                title: "Descarga el catálogo",
                desc: "Especificaciones técnicas, casos de éxito y opciones de materiales.",
                cta: "Descargar PDF",
                href: "/#catalogo",
                external: false,
              },
              {
                Icon: Instagram,
                title: "Síguenos en Instagram",
                desc: "Proyectos en obra, before/after, y novedades técnicas.",
                cta: "@eclipsecubiertasmx",
                href: "https://instagram.com/eclipsecubiertasmx",
                external: true,
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                {...fadeUp(0.15 + i * 0.1)}
                className="group border border-[#DDE2D5] p-7 md:p-8 hover:border-[#8cb62c] transition-colors"
              >
                <card.Icon className="w-8 h-8 text-[#8cb62c]" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-extrabold text-lg text-[#1F2937]">{card.title}</h3>
                <p className="mt-2 text-[#4B5563] text-[14px] leading-relaxed">{card.desc}</p>
                {card.external ? (
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-1 text-[#3D5311] font-mono text-[12px] uppercase tracking-wider font-semibold hover:text-[#8cb62c] transition-colors"
                  >
                    {card.cta}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </a>
                ) : (
                  <Link
                    to={card.href}
                    className="mt-5 inline-flex items-center gap-1 text-[#3D5311] font-mono text-[12px] uppercase tracking-wider font-semibold hover:text-[#8cb62c] transition-colors"
                  >
                    {card.cta}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          {/* contact line */}
          <motion.div {...fadeUp(0.4)} className="mt-14 text-center text-[#6B7280] text-sm">
            ¿Algún imprevisto? Escríbenos a{" "}
            <a href={`mailto:${BRAND.emailSales}`} className="text-[#3D5311] underline hover:text-[#8cb62c]">{BRAND.emailSales}</a>
            {" "}o llama al{" "}
            <a href={`tel:${BRAND.phoneTel}`} className="text-[#3D5311] underline hover:text-[#8cb62c]">{BRAND.phoneDisplay}</a>.
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
